/**
 * FleetHealthScore — reusable score display component
 * Used in Ops Console (Customer Detail + Customer List) and Client Portal
 */
import { Loader2 } from 'lucide-react';

const COLOR_MAP = {
  green:   { text: 'text-[#57EB31]', bg: 'bg-[#57EB31]/10 border-[#57EB31]/30', bar: 'bg-[#57EB31]', ring: '#57EB31' },
  teal:    { text: 'text-teal-400',  bg: 'bg-teal-900/20 border-teal-800/50',   bar: 'bg-teal-400',  ring: '#2DD4BF' },
  yellow:  { text: 'text-[#3B82F6]', bg: 'bg-[#3B82F6]/10 border-[#3B82F6]/30', bar: 'bg-[#3B82F6]', ring: '#3B82F6' },
  orange:  { text: 'text-orange-400', bg: 'bg-orange-900/20 border-orange-800/50', bar: 'bg-orange-400', ring: '#F97316' },
  red:     { text: 'text-red-400',   bg: 'bg-red-900/20 border-red-800/50',     bar: 'bg-red-400',   ring: '#FF3B30' },
};

const CONFIDENCE_LABEL = { low: '⚠️ Low confidence', medium: 'Medium confidence', high: 'High confidence' };

function BreakdownRow({ item }) {
  const pct = Math.round((item.score / item.max) * 100);
  const color = pct >= 80 ? 'bg-[#57EB31]' : pct >= 55 ? 'bg-[#3B82F6]' : 'bg-red-400';
  return (
    <div className="py-2.5 border-b border-[#2D3F5E]/40 last:border-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-['IBM_Plex_Sans'] text-[#B8C0CC]">{item.label}</span>
        <span className="font-['JetBrains_Mono'] text-xs font-bold text-white">{item.score}<span className="text-[#5C6A7A]">/{item.max}</span></span>
      </div>
      <div className="h-1.5 bg-[#0F172A] rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans'] mt-1">{item.detail}</p>
    </div>
  );
}

/** Compact badge for the customers list */
export function HealthScoreBadge({ score, label, color, loading }) {
  const c = COLOR_MAP[color] || COLOR_MAP.yellow;
  if (loading) return <Loader2 size={12} className="text-[#5C6A7A] animate-spin" />;
  if (score == null) return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold border font-['JetBrains_Mono'] bg-slate-800/40 border-slate-700/50 text-slate-400" title="Score will appear once this customer has any repair, PM, or DOT activity">
      —<span className="font-normal text-[9px] opacity-70">No Data</span>
    </span>
  );
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border font-['JetBrains_Mono'] ${c.bg} ${c.text}`}>
      {score}
      <span className="font-normal text-[10px] opacity-70">{label}</span>
    </span>
  );
}

/** Full card for Customer Detail / Portal Dashboard */
export default function FleetHealthScore({ data, loading, compact = false, onShare }) {
  if (loading) return (
    <div className="card-industrial p-6 flex items-center justify-center h-32">
      <Loader2 className="text-[#3B82F6] animate-spin" size={24} />
    </div>
  );
  if (!data) return null;

  // No data yet — show empty state instead of fake score
  if (data.score == null) {
    return (
      <div className="card-industrial p-6 text-center">
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-2">// FLEET HEALTH</p>
        <p className="text-white font-['Manrope'] font-bold text-sm mb-1">No Data Yet</p>
        <p className="text-[11px] text-[#8B9AAB] font-['IBM_Plex_Sans']">
          {data.message || 'Score will appear once this customer has any repair, PM, or DOT activity.'}
        </p>
        <p className="text-[10px] text-[#5C6A7A] font-['JetBrains_Mono'] mt-2 uppercase tracking-wider">
          {data.truck_count || 0} units · 0 data points
        </p>
      </div>
    );
  }

  const c = COLOR_MAP[data.color] || COLOR_MAP.yellow;
  const bd = data.breakdown || {};

  return (
    <div className="card-industrial p-5 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// FLEET HEALTH</p>
          <p className="text-white font-['Manrope'] font-bold text-sm">Fleet Health Score</p>
          <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans']">{data.period} · {data.truck_count} units · {data.total_repairs_90d} repairs</p>
        </div>
        {onShare && (
          <button onClick={onShare}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-[#2D3F5E] text-[#8B9AAB] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono']">
            Share Report
          </button>
        )}
      </div>

      {/* Score ring */}
      <div className="flex items-center gap-6">
        <div className="relative flex-shrink-0">
          <svg width="88" height="88" viewBox="0 0 88 88">
            <circle cx="44" cy="44" r="38" fill="none" stroke="#1E293B" strokeWidth="8" />
            <circle cx="44" cy="44" r="38" fill="none" stroke={c.ring}
              strokeWidth="8" strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 38}`}
              strokeDashoffset={`${2 * Math.PI * 38 * (1 - data.score / 100)}`}
              transform="rotate(-90 44 44)"
              style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-2xl font-['JetBrains_Mono'] font-bold leading-none ${c.text}`}>{data.score}</span>
            <span className="text-[9px] text-[#5C6A7A] font-['JetBrains_Mono']">/100</span>
          </div>
        </div>
        <div className="flex-1">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border font-['JetBrains_Mono'] uppercase tracking-wider ${c.bg} ${c.text} mb-2`}>
            {data.label}
          </span>
          {/* Mini bar chart */}
          <div className="space-y-1.5">
            {Object.values(bd).map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <span className="text-[9px] text-[#5C6A7A] font-['IBM_Plex_Sans'] w-20 flex-shrink-0 truncate">{item.label}</span>
                <div className="flex-1 h-1.5 bg-[#0F172A] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${Math.round(item.score/item.max*100) >= 80 ? 'bg-[#57EB31]' : Math.round(item.score/item.max*100) >= 55 ? 'bg-[#3B82F6]' : 'bg-red-400'}`}
                    style={{ width: `${Math.round(item.score/item.max*100)}%`, transition: 'width 0.7s ease' }}
                  />
                </div>
                <span className="text-[9px] font-['JetBrains_Mono'] text-[#5C6A7A] w-8 text-right">{item.score}/{item.max}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Confidence */}
      {data.confidence === 'low' && (
        <div className="px-3 py-2 bg-amber-900/20 border border-amber-800/50 rounded-sm text-[10px] text-amber-400 font-['IBM_Plex_Sans']">
          {CONFIDENCE_LABEL[data.confidence]} — less than 5 data points. Score improves with more repair history.
        </div>
      )}

      {/* Full breakdown (expanded) */}
      {!compact && (
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-2">Breakdown</p>
          {Object.values(bd).map(item => <BreakdownRow key={item.label} item={item} />)}
        </div>
      )}
    </div>
  );
}
